// src/pages/VideoPlayerPage.js - ЦЕЛОСНА СТРАНИЦА ЗА ВИДЕО ПЛЕЕР
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { videoAPI } from '../services/api';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Alert,
    CircularProgress,
    Chip,
    IconButton,
    useTheme,
    Drawer,
    useMediaQuery,
    Fab,
    Paper
} from '@mui/material';
import {
    FitnessCenter,
    Schedule,
    EmojiEvents,
    SportsMartialArts,
    Restaurant,
    Close,
    Fullscreen,
    FullscreenExit,
    PlayArrow,
    Pause,
    VolumeUp,
    VolumeOff,
    ArrowBack,
    Menu as MenuIcon
} from '@mui/icons-material';
import UserNavbar from '../components/UserNavbar';
import SideMenu from '../components/SideMenu';

const VideoPlayerPage = () => {
    const { videoId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const theme = useTheme();
    const { isDarkMode } = useCustomTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

    const [video, setVideo] = useState(location.state?.video || null);
    const [day, setDay] = useState(location.state?.day || null);
    const [program, setProgram] = useState(location.state?.program || null);
    const [loading, setLoading] = useState(!video);
    const [error, setError] = useState('');
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const videoRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!video) {
            fetchVideoDetails();
        }
    }, [videoId]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const fetchVideoDetails = async () => {
        try {
            setLoading(true);
            console.log('🎬 Fetching video details for ID:', videoId);
            const videoData = await videoAPI.getById(videoId);
            setVideo(videoData);
        } catch (err) {
            console.error('❌ Error fetching video:', err);
            setError('Грешка при вчитување на видеото.');
        } finally {
            setLoading(false);
        }
    };

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    const handleVideoClick = () => {
        togglePlay();
    };

    const menuItems = [
        {
            id: 1,
            title: 'Купи Програми',
            description: 'Прегледај и купи тренинг програми креирани од експерти за максимални резултати',
            icon: <FitnessCenter sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/buy-programs',
            color: '#ff7eb9',
            available: true,
            badge: 'Популарно'
        },
        // 👇 ОТСТРАНЕТО - Купи Тренинг во Живо
        {
            id: 2,
            title: 'Купи Диета',
            description: 'Здравствени диететски планови прилагодени за вашите фитнес цели',
            icon: <Restaurant sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/user-diets',
            color: '#4CAF50',
            available: true,
            badge: 'Препорачано'
        },
        {
            id: 3,
            title: 'Дневен Распоред',
            description: 'Вашиот персонализиран дневен распоред за вежбање и следење на прогресот',
            icon: <Schedule sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/schedule',
            color: '#c8f0cc',
            available: false,
            badge: 'Наскоро'
        },
        {
            id: 4,
            title: 'Достигнувања',
            description: 'Следете ги вашите достигнувања, прогресија и фитнес милници',
            icon: <EmojiEvents sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/achievements',
            color: '#ffd166',
            available: false,
            badge: 'Наскоро'
        }
    ];

    const handleItemClick = (item) => {
        setSelectedItem(item);
        if (isMobile) {
            setMobileMenuOpen(false);
        }
        if (!item.available) {
            alert('🚧 Оваа функционалност е во развој и наскоро ќе биде достапна!');
            return;
        }
        navigate(item.path);
    };

    const handleItemHover = (item) => {
        if (!isMobile) {
            setSelectedItem(item);
        }
    };

    const handleBack = () => {
        if (program) {
            navigate(`/program/${program.id}/days`);
        } else {
            navigate(-1);
        }
    };

    if (loading) {
        return (
            <>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}>
                    <UserNavbar />
                </Box>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh',
                    backgroundColor: 'background.default'
                }}>
                    <CircularProgress sx={{ color: 'primary.main' }} />
                </Box>
            </>
        );
    }

    if (error || !video) {
        return (
            <>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}>
                    <UserNavbar />
                </Box>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh',
                    backgroundColor: 'background.default',
                    px: 2
                }}>
                    <Card sx={{ maxWidth: 500 }}>
                        <CardContent>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error || 'Видеото не е пронајдено'}
                            </Alert>
                            <Button fullWidth variant="contained" onClick={handleBack}>
                                Назад
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </>
        );
    }

    const videoUrl = `${process.env.REACT_APP_API_URL}/videos/file/${video.id}`;

    return (
        <>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}>
                <UserNavbar />
            </Box>

            <Box sx={{
                display: 'flex',
                minHeight: '100vh',
                backgroundColor: 'background.default'
            }}>
                {/* Desktop/Tablet Sidebar */}
                {!isMobile && (
                    <Box sx={{
                        width: isTablet ? '280px' : '320px',
                        backgroundColor: 'background.paper',
                        borderRight: 1,
                        borderColor: 'divider',
                        flexShrink: 0,
                        position: 'sticky',
                        top: '64px',
                        height: 'calc(100vh - 64px)',
                        overflowY: 'auto',
                        zIndex: 999
                    }}>
                        <SideMenu
                            menuItems={menuItems}
                            selectedItem={selectedItem}
                            onItemClick={handleItemClick}
                            onItemHover={handleItemHover}
                            programsCount={0}
                            userName={user?.email || 'DimitarKazz'}
                        />
                    </Box>
                )}

                {/* Mobile Drawer */}
                {isMobile && (
                    <>
                        <Drawer
                            anchor="left"
                            open={mobileMenuOpen}
                            onClose={() => setMobileMenuOpen(false)}
                            PaperProps={{
                                sx: {
                                    width: '280px',
                                    backgroundColor: 'background.paper',
                                    borderRight: 1,
                                    borderColor: 'divider'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                                <IconButton onClick={() => setMobileMenuOpen(false)}>
                                    <Close />
                                </IconButton>
                            </Box>
                            <SideMenu
                                menuItems={menuItems}
                                selectedItem={selectedItem}
                                onItemClick={handleItemClick}
                                onItemHover={handleItemHover}
                                programsCount={0}
                                userName={user?.email || 'DimitarKazz'}
                            />
                        </Drawer>

                        <Fab
                            color="primary"
                            onClick={() => setMobileMenuOpen(true)}
                            sx={{
                                position: 'fixed',
                                bottom: 16,
                                left: 16,
                                zIndex: 1000
                            }}
                        >
                            <MenuIcon />
                        </Fab>
                    </>
                )}

                {/* Main Content */}
                <Box sx={{
                    flex: 1,
                    p: { xs: 2, sm: 3, md: 4 },
                    backgroundColor: 'background.default',
                    overflow: 'auto'
                }}>
                    {/* Back Button */}
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={handleBack}
                        sx={{ mb: 3 }}
                    >
                        Назад кон {program ? program.name : 'програмата'}
                    </Button>

                    {/* Video Player Container */}
                    <Box
                        ref={containerRef}
                        sx={{
                            position: 'relative',
                            width: '100%',
                            maxWidth: 1200,
                            mx: 'auto',
                            mb: 4,
                            backgroundColor: '#000',
                            borderRadius: isFullscreen ? 0 : 2,
                            overflow: 'hidden',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                        }}
                    >
                        {/* Video Element */}
                        <video
                            ref={videoRef}
                            src={videoUrl}
                            poster={`${process.env.REACT_APP_API_URL}/videos/${video.id}/thumbnail`}
                            onClick={handleVideoClick}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                                cursor: 'pointer'
                            }}
                            controls={false}
                        />

                        {/* Video Controls Overlay */}
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                opacity: 0,
                                transition: 'opacity 0.3s',
                                '&:hover': {
                                    opacity: 1
                                }
                            }}
                        >
                            <IconButton onClick={togglePlay} sx={{ color: 'white' }}>
                                {isPlaying ? <Pause /> : <PlayArrow />}
                            </IconButton>

                            <IconButton onClick={toggleMute} sx={{ color: 'white' }}>
                                {isMuted ? <VolumeOff /> : <VolumeUp />}
                            </IconButton>

                            <Box sx={{ flex: 1 }} />

                            <IconButton onClick={toggleFullscreen} sx={{ color: 'white' }}>
                                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                            </IconButton>
                        </Box>

                        {/* Play Button Overlay (when paused) */}
                        {!isPlaying && (
                            <Box
                                onClick={handleVideoClick}
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    cursor: 'pointer'
                                }}
                            >
                                <IconButton
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        width: 80,
                                        height: 80,
                                        '&:hover': {
                                            backgroundColor: 'white'
                                        }
                                    }}
                                >
                                    <PlayArrow sx={{ fontSize: 48, color: '#333' }} />
                                </IconButton>
                            </Box>
                        )}
                    </Box>

                    {/* Video Info */}
                    <Card sx={{ maxWidth: 1200, mx: 'auto', mb: 4 }}>
                        <CardContent>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                                {video.title}
                            </Typography>

                            {day && (
                                <Chip
                                    label={day.title}
                                    color="primary"
                                    sx={{ mb: 2, mr: 1 }}
                                />
                            )}

                            {program && (
                                <Chip
                                    label={program.name}
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                />
                            )}

                            {video.description && (
                                <Typography variant="body1" sx={{ color: 'text.secondary', mt: 2 }}>
                                    {video.description}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>

                    <Box sx={{ height: { xs: '80px', md: '100px' } }} />
                </Box>
            </Box>
        </>
    );
};

export default VideoPlayerPage;